import { useEffect } from "react";

/**
 * Google Analytics 4 (gtag.js) – consent-gated.
 * - Reads REACT_APP_GA_MEASUREMENT_ID env var (e.g. "G-XXXXXXXXXX")
 * - Only loads gtag.js if the cookie consent choice is "accepted"
 * - Listens for consent-change events so it can boot on the fly
 * - Silently no-ops if no measurement ID configured (safe for local dev)
 */
const STORAGE_KEY = "freeayto_cookie_consent_v1";
let injected = false;

const readConsent = () => {
  try {
    const raw = localStorage.getItem(STORAGE_KEY);
    if (!raw) return null;
    const parsed = JSON.parse(raw);
    return parsed?.choice || null;
  } catch {
    return null;
  }
};

const injectGA = (id) => {
  if (injected) return;
  if (typeof document === "undefined") return;
  injected = true;

  const script = document.createElement("script");
  script.async = true;
  script.src = `https://www.googletagmanager.com/gtag/js?id=${id}`;
  document.head.appendChild(script);

  window.dataLayer = window.dataLayer || [];
  window.gtag = function () {
    window.dataLayer.push(arguments);
  };
  window.gtag("js", new Date());
  window.gtag("config", id, { anonymize_ip: true });
};

const GoogleAnalytics = () => {
  const id = process.env.REACT_APP_GA_MEASUREMENT_ID;

  useEffect(() => {
    if (!id) return;

    const maybeInject = () => {
      const choice = readConsent();
      if (choice === "accepted") injectGA(id);
    };

    // Try immediately (in case user already consented before)
    maybeInject();

    // Listen for changes (e.g. same-tab cookie choice)
    const onStorage = (e) => {
      if (e.key === STORAGE_KEY) maybeInject();
    };
    const onConsent = () => maybeInject();

    window.addEventListener("storage", onStorage);
    window.addEventListener("freeayto:consent-updated", onConsent);
    return () => {
      window.removeEventListener("storage", onStorage);
      window.removeEventListener("freeayto:consent-updated", onConsent);
    };
  }, [id]);

  return null;
};

export default GoogleAnalytics;
